const GWP_PROPERTY = '_free_gift';
const GWP_DRAWER_DEBOUNCE = 150;
const GWP_DRAWER_MIN_INTERVAL = 1500;

class GwpController extends HTMLElement {
  connectedCallback() {
    this.running = false;
    this.rerunRequested = false;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'gwp') return;
      return this.evaluate();
    });

    this.watchDrawer();
    this.evaluate();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.drawerObserver) this.drawerObserver.disconnect();
    clearTimeout(this.drawerDebounce);
  }

  watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    this.drawerObserver = new MutationObserver(() => {
      if (!drawer.classList.contains('active')) return;
      this.scheduleDrawerEvaluate();
    });

    this.drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
  }

  // Collapse a burst of drawer mutations into a single evaluate, and keep a floor
  // between DOM-triggered evaluates so a chatty third-party widget rendering inside
  // the drawer cannot drip /cart.js requests. cartUpdate-driven evaluates bypass this.
  scheduleDrawerEvaluate() {
    const sinceLast = Date.now() - (this.lastEvaluateAt || 0);
    const delay = Math.max(GWP_DRAWER_DEBOUNCE, GWP_DRAWER_MIN_INTERVAL - sinceLast);

    clearTimeout(this.drawerDebounce);
    this.drawerDebounce = setTimeout(() => this.evaluate(), delay);
  }

  get enabled() {
    return this.dataset.enabled === 'true';
  }

  get variantId() {
    return Number(this.dataset.variantId) || null;
  }

  get threshold() {
    const amount = parseFloat(this.dataset.threshold);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return Math.round(amount * 100);
  }

  // The drawer bar is re-rendered on every cart change, so its stock flag is fresher
  // than this element's page-load snapshot. It is only emitted while the promo is on.
  get liveInStock() {
    const bar = document.querySelector('.free-shipping-wrapper[data-gwp-in-stock]');
    return bar ? bar.dataset.gwpInStock === 'true' : null;
  }

  get inStock() {
    const live = this.liveInStock;
    if (live !== null) return live;

    if (this.dataset.available !== 'true') return false;
    const tracked = (this.dataset.inventoryManagement || '') !== '';
    if (!tracked) return true;
    if (this.dataset.inventoryPolicy === 'continue') return true;
    return Number(this.dataset.inventoryQuantity || 0) > 0;
  }

  async evaluate() {
    if (this.running) {
      this.rerunRequested = true;
      return;
    }

    this.running = true;

    try {
      let passes = 0;
      do {
        this.rerunRequested = false;
        const changed = await this.reconcile();
        if (changed) this.rerunRequested = true;
        passes += 1;
      } while (this.rerunRequested && passes < 3);
    } catch (error) {
      console.error('[gwp]', error);
    } finally {
      this.running = false;
      this.lastEvaluateAt = Date.now();
    }
  }

  async reconcile() {
    const cart = await this.fetchCart();
    if (!cart || !Array.isArray(cart.items)) return false;

    const giftLines = cart.items.filter(
      (item) =>
        (item.properties && String(item.properties[GWP_PROPERTY]) === 'true') ||
        (this.variantId !== null && item.variant_id === this.variantId),
    );

    const giftValue = giftLines.reduce((total, item) => total + (item.final_line_price || 0), 0);
    const qualifyingTotal = cart.total_price - giftValue;

    const threshold = this.threshold;
    const qualifies =
      this.enabled && this.variantId !== null && threshold !== null && this.inStock && qualifyingTotal >= threshold;

    if (!qualifies) {
      if (!giftLines.length) return false;
      return this.removeLines(giftLines);
    }

    const stale = giftLines.filter((item) => item.variant_id !== this.variantId || item.quantity !== 1);
    const valid = giftLines.filter((item) => stale.indexOf(item) === -1);
    const surplus = valid.slice(1);
    const toRemove = stale.concat(surplus);

    if (toRemove.length) return this.removeLines(toRemove);

    if (!valid.length) return this.addGift();

    return false;
  }

  fetchCart() {
    return fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => (response.ok ? response.json() : null));
  }

  async addGift() {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: this.variantId,
            quantity: 1,
            properties: { [GWP_PROPERTY]: 'true' },
          },
        ],
      }),
    });

    if (!response.ok) {
      console.warn('[gwp] could not add gift', await response.text());
      return false;
    }

    await this.publishCartUpdate();
    return true;
  }

  async removeLines(lines) {
    let changed = false;

    for (const line of lines) {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });

      if (!response.ok) {
        console.warn('[gwp] could not remove gift line', await response.text());
        break;
      }

      changed = true;
    }

    if (changed) await this.publishCartUpdate();
    return changed;
  }

  async publishCartUpdate() {
    const cart = await this.fetchCart();
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp', cartData: cart });
    this.refreshCartCount();
  }

  refreshCartCount() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.updateCartState === 'function') {
      drawer.updateCartState();
      return;
    }

    fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const current = document.querySelector('#cart-count-bubble');
        const updated = html.querySelector('#cart-count-bubble');

        if (current && updated) {
          current.innerHTML = updated.innerHTML;
        } else if (updated) {
          const target = document.querySelector('.is-cart');
          if (target) target.appendChild(updated);
        }
      })
      .catch((error) => console.error('[gwp] could not refresh cart count', error));
  }
}

customElements.define('gwp-controller', GwpController);
